import { numberPrefixedStringSorter, SMap } from '@avantia/client-and-server-utilities';
import { lodash } from '@avantia/lodash';
import { createControlId, destructureControlId } from '@avantia/questionset-model';
import jQuery from 'jquery';
import { CSSProperties } from 'react';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import { getQuestionIdFor, toLowerCase } from '../../../tools/clientAndServerUtilities';
import { KeyPrefix } from '../../../tools/eventLogger';
import { convertSkewerToCamelCase, getTextFromLookupMap } from '../../../tools/utilities';
import { HpClientAnswerMap, HpClientQuestionLookupItemMap, HpClientQuestionUI } from '../../../types';
import { HpEventDetail, HpOnMouseEvent } from '../../../types/inputControlTypes';
import { addKeyboardNavigationHandler, getEventKeyConstants } from '../controlEvents';
import { iterateLookupMap } from '../controlUtilities';
import { createMouseEvent } from '../inputControl';

export interface HpBasicDropDownListControlProps {
  answers: HpClientAnswerMap;
  className?: string;
  disabled?: boolean;
  eventDetail: HpEventDetail;
  id: string;
  lookup: HpClientQuestionLookupItemMap;
  name: string | undefined;
  onClick: HpOnMouseEvent;
  placeholder?: string;
  productId: string;
  sortItemsBy: string | undefined;
  style?: CSSProperties;
  textProperty?: string;
  title?: string;
  ui: HpClientQuestionUI;
  value: string;
}

const { isIe } = getEventKeyConstants();
const dropDownCssSelector = '.dropdown';
const dropDownItemCssSelector = '.dropdown-item';

export function BasicDropDownListControl({
  answers,
  className,
  disabled,
  eventDetail,
  id,
  lookup,
  name,
  onClick,
  placeholder,
  productId,
  sortItemsBy,
  style,
  textProperty,
  title,
  ui,
  value
}: HpBasicDropDownListControlProps): JSX.Element {
  if (!lookup) {
    throw new Error(`There is no lookup data for question ${id}.`);
  }

  let items: HpOption[] = [];
  if (!Object.hasOwn(lookup, value)) {
    const missingOptionsId = getQuestionIdFor(productId, 'missingOptions', false);
    if (missingOptionsId) {
      const itemKey = createControlId({ questionId: id, key: value });
      const missingOptionMap = answers[missingOptionsId] as unknown as SMap<string>;
      let optionText = `${value}`;
      if (missingOptionMap) {
        let { questionId: key } = destructureControlId(id, { requirePrefix: false });
        key = convertSkewerToCamelCase(lodash.last(key.split('_')));
        const text = missingOptionMap[`${key}:${value}`];
        if (text !== undefined) {
          optionText = text;
        }
      }

      if (optionText.startsWith(KeyPrefix)) {
        optionText = optionText.substring(KeyPrefix.length);
      }

      items.push({ itemKey, optionText });
      title = optionText;
    }
  }

  iterateLookupMap(lookup, ui, (option, key) => {
    const optionText = getTextFromLookupMap(key, lookup, textProperty);
    if (key === value) {
      title = optionText;
    }

    const itemKey = createControlId({ questionId: id, key });
    items.push({ itemKey, optionText });
  });

  if (sortItemsBy === 'numberPrefixedStringSorter') {
    items = items.sort((a, b) => numberPrefixedStringSorter(a.optionText, b.optionText));
  }

  const options = items.map(({ itemKey, optionText }) => {
    return (
      <Dropdown.Item
        key={itemKey}
        as="button"
        id={itemKey}
        onClick={createMouseEvent(onClick, eventDetail) as any}
        onKeyPress={handleKeyPressEvent}
      >
        {optionText}
      </Dropdown.Item>
    );
  });

  const controlId = createControlId({ questionId: id });
  const titleText = title || placeholder || 'Please select';
  return (
    <DropdownButton
      key={controlId}
      id={controlId}
      {...{ name: name || id }}
      title={titleText}
      style={style}
      className={className}
      disabled={disabled === true}
      variant={value ? 'primary' : 'outline-primary'}
      onKeyPress={handleKeyPressEvent}
    >
      {options}
    </DropdownButton>
  );
}

/**
 * Jumps to an item with text that starts with the pressed key.
 * If an item is selected that already matches, the next one will be selected.
 * @param event keyboard event
 */
export function handleKeyPressEvent(event: React.KeyboardEvent<HTMLButtonElement>): void {
  event.stopPropagation();

  const $ = jQuery;
  const key = toLowerCase(event.key);
  const items = $(dropDownItemCssSelector, $(event.currentTarget).closest(dropDownCssSelector)).toArray();
  const matches = items.filter((item) => toLowerCase($(item).text()).startsWith(key));
  const focussed = matches.filter((m) => $(m).is(':focus'))[0];
  if (matches.length) {
    let focusIx = 0;
    if (focussed) {
      const ix = matches.indexOf(focussed);
      if (ix >= 0 && matches[ix + 1]) {
        focusIx = ix + 1;
      }
    }

    matches[focusIx].focus();
  }
}

export function addBasicDropDownKeyboardNavigationHandler(window: Window, jQuery: JQueryStatic): void {
  if (isIe) {
    addKeyboardNavigationHandler({
      controlContainerCssSelector: dropDownCssSelector,
      controlItemCssSelector: dropDownItemCssSelector,
      window,
      jQuery
    });
  }
}

interface HpOption {
  itemKey: string;
  optionText: string;
}
